import { useEffect, useRef } from "react";

// Lightweight rich-text editor + viewer, no dependencies. Emits HTML. Supports
// bold/italic/underline, highlight, font size, bullet + numbered lists, and
// hyperlinks. Output is sanitized before it's rendered or sent (see below), so a
// stored/emailed body can never carry scripts or event handlers.

const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "S", "STRIKE", "A", "UL", "OL", "LI", "P", "BR", "SPAN", "DIV", "FONT", "H1", "H2", "H3", "BLOCKQUOTE"]);
const ALLOWED_ATTR: Record<string, string[]> = { A: ["href", "target", "rel"], SPAN: ["style"], FONT: ["size", "color"], DIV: ["style"], P: ["style"], LI: ["style"] };
const STYLE_OK = /^(color|background|background-color|font-size|font-weight|font-style|text-decoration)$/i;

// Strip everything except a safe formatting allowlist; force links to open in a
// new tab and drop javascript: URLs and on* handlers.
export function sanitizeHtml(html: string): string {
  if (!html) return "";
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const walk = (node: ParentNode) => {
    Array.from(node.children).forEach((child) => {
      if (!ALLOWED_TAGS.has(child.tagName)) { child.replaceWith(...Array.from(child.childNodes)); return; }
      const allowed = ALLOWED_ATTR[child.tagName] || [];
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on") || !allowed.includes(name)) { child.removeAttribute(attr.name); return; }
        if (name === "href") {
          if (/^\s*javascript:/i.test(attr.value)) child.removeAttribute("href");
          else { child.setAttribute("target", "_blank"); child.setAttribute("rel", "noreferrer noopener"); }
        }
        if (name === "style") {
          const cleaned = attr.value.split(";").map(s => s.trim()).filter(Boolean)
            .filter(s => STYLE_OK.test(s.split(":")[0]?.trim() || "")).join("; ");
          if (cleaned) child.setAttribute("style", cleaned); else child.removeAttribute("style");
        }
      });
      walk(child);
    });
  };
  walk(tpl.content);
  return tpl.innerHTML;
}

// Plain-text fallback (for email text parts + activity logs + previews).
export function htmlToText(html: string): string {
  const tpl = document.createElement("template");
  tpl.innerHTML = (html || "").replace(/<\/(p|div|li|h[1-3]|blockquote)>/gi, "\n").replace(/<br\s*\/?>/gi, "\n");
  return (tpl.content.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

function Btn({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return <button type="button" title={title} onMouseDown={e => { e.preventDefault(); onClick(); }}
    className="flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-sm text-gray2 hover:bg-offwhite hover:text-teal-dark">{children}</button>;
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 140 }: { value: string; onChange: (html: string) => void; placeholder?: string; minHeight?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  // Sync external changes (e.g. picking a template) without stomping the caret
  // while the user is actively typing in this editor.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || "")) el.innerHTML = value || "";
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");
  const exec = (cmd: string, arg?: string) => { ref.current?.focus(); document.execCommand(cmd, false, arg); emit(); };
  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    const href = /^(https?:|mailto:|\/)/i.test(url) ? url : `https://${url}`;
    exec("createLink", href);
  };

  return (
    <div className="rounded-lg border border-line bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-1.5 py-1">
        <Btn onClick={() => exec("bold")} title="Bold"><b>B</b></Btn>
        <Btn onClick={() => exec("italic")} title="Italic"><i>I</i></Btn>
        <Btn onClick={() => exec("underline")} title="Underline"><u>U</u></Btn>
        <Btn onClick={() => exec("hiliteColor", "#fde68a")} title="Highlight"><span className="rounded bg-[#fde68a] px-1">H</span></Btn>
        <span className="mx-1 h-4 w-px bg-line" />
        <select title="Font size" defaultValue="" onMouseDown={e => e.stopPropagation()} onChange={e => { if (e.target.value) exec("fontSize", e.target.value); e.currentTarget.value = ""; }}
          className="h-7 rounded border border-line bg-white px-1 text-xs text-gray2">
          <option value="">Size</option>
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="6">Huge</option>
        </select>
        <span className="mx-1 h-4 w-px bg-line" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bullet list">•≡</Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list">1.≡</Btn>
        <span className="mx-1 h-4 w-px bg-line" />
        <Btn onClick={addLink} title="Insert link">🔗</Btn>
        <Btn onClick={() => exec("removeFormat")} title="Clear formatting">⌫</Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder || "Write here…"}
        className="rich-editor px-3 py-2 text-sm outline-none"
        style={{ minHeight }}
      />
    </div>
  );
}

// Render stored rich text safely.
export function RichTextView({ html, className }: { html: string; className?: string }) {
  return <div className={`rich-content ${className || ""}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
}

// Display helper that handles BOTH old plain-text and new rich HTML for the same
// field — renders formatted HTML when present, otherwise preserves line breaks.
// Lets a field be upgraded to rich text without breaking existing plain entries.
export function AutoText({ text, className }: { text?: string | null; className?: string }) {
  if (!text) return null;
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  return isHtml
    ? <RichTextView html={text} className={className} />
    : <div className={`whitespace-pre-wrap ${className || ""}`}>{text}</div>;
}
